import { useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { saveMonthlyPlanSelected } from '../actions';

import { isPaycheck } from '../util';
import accidentAndIllness from '../assets/plans/icon-heart-beat.svg';
import accidentOnly from '../assets/plans/icon-heart-shine.svg';
import { useProp103Features } from './useProp103Features';
import { PROP103_PLAN_TYPE } from '../constants';

const useCalculatePlanPrice = () => {
  const dispatch = useDispatch();
  const store = useSelector(({ quoting }) => quoting);
  const isProp103 = useProp103Features();

  const {
    petQuoteSelected,
    quoteSelectedValues,
    data,
  } = store;

  const { petQuote, plans, plansPackaged } = useMemo(() => {
    if (!store.data) {
      return { plans: [], plansPackaged: store.plansPackaged };
    }

    const currentPetQuote = store.data.ebPetQuoteResponseList
      .find((item) => item.petQuoteId === petQuoteSelected);

    const plansQuote = currentPetQuote ? currentPetQuote.Plans : [];

    let plansPackagedSelected = [];

    if (currentPetQuote) {
      plansPackagedSelected = currentPetQuote.PrePackagedPlans?.map((item) => {
        const plan = currentPetQuote.Plans
          .find((planItem) => planItem.Plan === item.PlanId);
        const ratingOption = plan.RatingOptions
          .find((ratingItem) => ratingItem.DeductibleId
            === item.DeductibleId && ratingItem.ReimbursementId
            === item.ReimbursementId);
        return ({
          ...item,
          deductibleName: ratingOption.DeductibleName,
          maxAnnual: plan.MaxAnnual,
          monthlyPrice: ratingOption.MonthlyPremium,
          reimbursementName: ratingOption.ReimbursementName,
        });
      });
    }

    return {
      petQuote: currentPetQuote,
      plans: plansQuote,
      plansPackaged: plansPackagedSelected,
    };
  }, [petQuoteSelected, store.data, store.plansPackaged]);

  const quoteSelected = useMemo(() => quoteSelectedValues
    .find((item) => item.quoteId === petQuoteSelected)
    || { petName: 'Pet Name' }, [petQuoteSelected, quoteSelectedValues]);

  const getCurrentPlan = useCallback(() => {
    const emptyPlan = {
      annualAmount: 0,
      getValue: () => 0,
      monthlyAmount: 0,
      partnerAnnualSaving: 0,
    };

    if (!petQuote) {
      return emptyPlan;
    }

    const currPlan = petQuote.Plans
      .find((plan) => plan.Plan === quoteSelected.selectedPlan);
    if (!currPlan) {
      return emptyPlan;
    }
    const price = currPlan.RatingOptions
      .find((rating) => rating.DeductibleId === quoteSelected.deductibleId
        && rating.ReimbursementId === quoteSelected.reimbursementId);

    if (!price) {
      return emptyPlan;
    }

    let getValue = () => price.MonthlyPremium;

    if (isPaycheck) {
      const priceAnnualFix = (price.MonthlyPremium * 12);
      getValue = () => (priceAnnualFix * 0.9) / 24;
    }

    return {
      annualAmount: price.AnnualPremium,
      getValue,
      monthlyAmount: isPaycheck ? getValue() : price.MonthlyPremium,
      partnerAnnualSaving: price.PartnerAnnualSaving,
    };
  }, [petQuote, quoteSelected]);

  const planTypeInfo = useMemo(() => {
    if (isProp103 && data) {
      const { PlanTypes } = data;

      return PlanTypes.map((planInfo) => ({
        coveredItems: planInfo.WhatsCovered
          ?.map((wc) => wc.Description) || [],
        description: planInfo.Subtext,
        icon: planInfo.Id === PROP103_PLAN_TYPE.ACCIDENT_AND_ILLNESS
          ? accidentAndIllness
          : accidentOnly,
        id: planInfo.Id,
        monthly: true,
        title: planInfo.Plan,
      }));
    }
    return [];
  }, [data, isProp103]);

  const getPlanTypePrice = useCallback((planType) => {
    if (isProp103 && petQuote) {
      if (petQuote.PlanType === planType) {
        const selectedPlan = plansPackaged
          .find((plan) => plan.Id === quoteSelected?.selectedPlanPackaged)
          ?.monthlyPrice || quoteSelected.totalMontly;
        return selectedPlan || 0;
      }
      const notSelectedPlan = petQuote.NoSelectedPlanInfo?.Plans
        ?.find?.((plan) => plan.Plan === quoteSelected.selectedPlan);

      if (!notSelectedPlan) {
        return 0;
      }

      const notSelectedOption = notSelectedPlan.RatingOptions
        .find((rating) => rating.DeductibleId === quoteSelected.deductibleId
          && rating.ReimbursementId === quoteSelected.reimbursementId);
      return notSelectedOption?.MonthlyPremium || 0;
    }
    return 0;
  }, [isProp103, petQuote, plansPackaged, quoteSelected]);

  useEffect(() => {
    const price = getCurrentPlan();

    if (quoteSelected.totalMontly !== undefined
      && quoteSelected.totalMontly !== price.getValue()) {
      dispatch(saveMonthlyPlanSelected(price));
    }
  }, [
    dispatch,
    getCurrentPlan,
    quoteSelected,
  ]);

  return {
    getPlanTypePrice,
    petQuote,
    plans,
    plansPackaged,
    planTypeInfo,
    quoteSelected,
  };
};

export { useCalculatePlanPrice };
